/**
 * Utility functions for handling API errors, especially validation errors
 */

/**
 * Check if an error is a validation error
 * Validation errors have status 400 and contain an errors array
 * @param {Error} error - The error object from axios
 * @returns {boolean} - True if it's a validation error
 */
export const isValidationError = (error) => {
  return (
    error?.response?.status === 400 &&
    Array.isArray(error?.response?.data?.errors) &&
    error.response.data.errors.length > 0
  );
};

/**
 * Format field name from snake_case or camelCase to readable format
 * @param {string} field - Field name (e.g., 'full_name', 'contactPrimary')
 * @returns {string} - Formatted field name (e.g., 'Full Name', 'Contact Primary')
 */
const formatFieldName = (field) => {
  if (!field) return 'Field';
  
  // Replace underscores and camelCase with spaces
  return field
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Format validation errors into a user-friendly message
 * @param {Error} error - The error object from axios
 * @returns {string} - Formatted error message
 */
export const formatValidationErrors = (error) => {
  // Check if it's a validation error
  if (isValidationError(error)) {
    const errors = error.response.data.errors;
    const errorMessages = errors.map(err => {
      const fieldName = formatFieldName(err.field || err.path || 'Field');
      return `• ${fieldName}: ${err.message || err.msg || 'Invalid value'}`;
    });
    
    return `Validation failed:\n${errorMessages.join('\n')}`;
  }
  
  // For non-validation errors, return the message from response or fallback
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  // Fallback messages
  if (error?.response?.status === 401) {
    return 'Unauthorized. Please login again.';
  }
  
  if (error?.response?.status === 403) {
    return 'Access denied. You do not have permission.';
  }
  
  if (error?.response?.status >= 500) {
    return 'Server error. Please try again later.';
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return 'An error occurred. Please try again.';
};

/**
 * Extract validation errors as an array of objects
 * Useful for displaying errors inline next to form fields
 * @param {Error} error - The error object from axios
 * @returns {Array} - Array of error objects with field and message
 */
export const getValidationErrors = (error) => {
  if (isValidationError(error)) {
    return error.response.data.errors.map(err => ({
      field: err.field || err.path || '',
      message: err.message || err.msg || 'Invalid value'
    }));
  }
  return [];
};

